import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useTheme } from "styled-components";
import { SelectBox, Option } from "@monolith-forensics/monolith-ui";
import UserApi from "@/api/users/users";
import { UserOption } from "@/Features/ChainOfCustody/Coc.types";

// Full user object from API
interface User {
  user_id: number;
  full_name: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  title?: string;
  location_id: number;
}

interface UserCocSelectorProps {
  label?: string;
  required?: boolean;
  placeholder?: string;
  enabled?: boolean;
}

const UserContent: React.FC<{ user: User }> = ({ user }) => {
  const theme = useTheme() as { palette: { text: { secondary: string } } };

  if (!user.email) {
    return null;
  }

  return (
    <div
      style={{
        fontSize: "0.9em",
        marginTop: "4px",
        color: theme.palette.text.secondary,
        lineHeight: "1.2",
        display: "-webkit-box",
        WebkitLineClamp: 1,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      {user.email}
    </div>
  );
};

const UserCocSelector: React.FC<UserCocSelectorProps> = ({
  label = "User",
  required = false,
  placeholder = "Select user...",
  enabled = true,
  ...restProps
}) => {
  const { data: users } = useQuery({
    enabled: enabled,
    queryKey: [
      "users",
      "list",
      { includeObservers: false, includeInactive: false },
    ],
    queryFn: () =>
      UserApi.getUsers({ includeObservers: false, includeInactive: false }),
  });

  // Transform users for SelectBox
  const userOptions =
    users?.map((user: User) => ({
      label: user.full_name,
      value: {
        user_id: user.user_id,
        full_name: user.full_name,
        location_id: user.location_id,
      } as UserOption,
      data: user,
    })) || [];

  return (
    <SelectBox
      label={label}
      required={required}
      placeholder={placeholder}
      data={userOptions}
      searchable
      openOnFocus={false}
      renderOption={(item: string | Option) => {
        if (typeof item === "string") {
          return <div>{item}</div>;
        }
        return (
          <div style={{ padding: "5px 0" }}>
            <div>{item.label}</div>
            {item.data && <UserContent user={item.data} />}
          </div>
        );
      }}
      {...restProps}
    />
  );
};

export default UserCocSelector;
